import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { create } from "zustand";

import { TaskStatuses } from "@/constants/status";
import { IPlanPassenger, Task } from "@/types/tasks.types";

type ShowTaskUpdateDialogType = {
    id: string;
    current: TaskStatuses;
    next: TaskStatuses;
} | null;

type ShowBulkDialogType = {
    ids: string[];
} | null;

type ShowBulkUnassignDialogType =
    | (ShowBulkDialogType & {
          totalPassengers: number;
      })
    | null;

type TrackingFilters = {
    status: TaskStatuses | null;
    selectedDrivers: string[];
    search: string;
};

type TrackingTableState = {
    filters: TrackingFilters;
    isFullScreen: boolean;
    showTaskUpdateDialog: ShowTaskUpdateDialogType;
    selectedRows: GridRowSelectionModel;
    date: DateTime;
    expandedRows: GridRowId[];
    showBulkRegisterDialog: ShowBulkDialogType;
    showBulkCancelDialog: ShowBulkDialogType;
    showBulkUnassignDialog: ShowBulkUnassignDialogType;
};

type TrackingTableActions = {
    actions: {
        updateFilters: (filters: Partial<TrackingFilters>) => void;
        updateFullScreen: (val?: boolean) => void;
        updateShowTaskUpdateDialog: (val?: ShowTaskUpdateDialogType) => void;
        updateSelectedRows: (val?: GridRowSelectionModel) => void;
        setDate: (date: DateTime) => void;
        setExpandedRows: (ids: GridRowId[]) => void;
        handleExpandAll: (rows: Task<IPlanPassenger<"api">>[]) => void;
        updateShowBulkRegisterDialog: (val?: ShowBulkDialogType) => void;
        updateShowBulkCancelDialog: (val?: ShowBulkDialogType) => void;
        updateShowBulkUnassignDialog: (val?: ShowBulkUnassignDialogType) => void;
    };
};

export const usePlanTableStore = create<TrackingTableState & TrackingTableActions>()((set, get) => ({
    filters: {
        status: null,
        selectedDrivers: [],
        search: "",
    },
    showTaskUpdateDialog: null,
    isFullScreen: false,
    selectedRows: [],
    date: DateTime.now(),
    expandedRows: [],
    showBulkRegisterDialog: null,
    showBulkCancelDialog: null,
    showBulkUnassignDialog: null,
    actions: {
        updateFullScreen: (isFullScreen) => set({ isFullScreen: isFullScreen ?? !get().isFullScreen }),
        updateFilters: (filters) => {
            set({ filters: { ...get().filters, ...filters } });
        },
        updateShowTaskUpdateDialog: (val) => set({ showTaskUpdateDialog: val ?? null }),
        updateSelectedRows: (val) => set({ selectedRows: val }),
        setDate: (date) => set({ date }),
        setExpandedRows: (ids) => set({ expandedRows: ids }),
        handleExpandAll: (rows) => {
            const expandedRows = get().expandedRows;
            if (expandedRows.length === rows.length) {
                set({ expandedRows: [] });
            } else {
                set({ expandedRows: rows.map((row) => row.id) });
            }
        },
        updateShowBulkRegisterDialog: (val) => set({ showBulkRegisterDialog: val ?? null }),
        updateShowBulkCancelDialog: (val) => set({ showBulkCancelDialog: val ?? null }),
        updateShowBulkUnassignDialog: (val) => set({ showBulkUnassignDialog: val ?? null }),
    },
}));

export const useTrackingIsFullScreen = () => usePlanTableStore((state) => state.isFullScreen);
export const useTrackingFilters = () => usePlanTableStore((state) => state.filters);
export const useTrackingShowTaskUpdateDialog = () => usePlanTableStore((state) => state.showTaskUpdateDialog);
export const useTrackingSelectedRows = () => usePlanTableStore((state) => state.selectedRows);
export const useTrackingDate = () => usePlanTableStore((state) => state.date);
export const useTrackingExpandedRows = () => usePlanTableStore((state) => state.expandedRows);

export const useTrackingShowBulkRegisterDialog = () => usePlanTableStore((state) => state.showBulkRegisterDialog);
export const useTrackingShowBulkCancelDialog = () => usePlanTableStore((state) => state.showBulkCancelDialog);
export const useTrackingShowBulkUnassignDialog = () => usePlanTableStore((state) => state.showBulkUnassignDialog);

export const useTrackingActions = () => usePlanTableStore((state) => state.actions);
